import { DownloadIcon, PenLineIcon, TrashIcon, UploadIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { clearObjectUrl, downloadByUrl, getImageData } from '@/utils/file';
import { ACCEPTED_SIGNATURE_FILE_INPUT } from './signature-file.schema';
import { SignaturePadDialog } from './signature-pad-dialog';

/**
 * Who enters the signature: the doctor themself (`own`, pad only), or an admin
 * filling an attending's file (`admin`), who may also import a scan since an
 * admin-created attending may have no account to draw from.
 */
export type SignatureFormMode = 'own' | 'admin';

type SignatureFormProps = {
  form: any;
  fieldName: string;
  currentSignature: string | null | undefined;
  label: string;
  mode?: SignatureFormMode;
  /** Shown only in `admin` mode: doctors replace, they never delete. */
  onDeleteSignature?: () => void;
};

/**
 * Signature field: shows the current signature (drawn or legacy upload) and
 * opens the signature pad to draw a new one. The drawn PNG becomes the field
 * value; leaving the pad empty keeps the existing signature.
 *
 * @param props - See {@link SignatureFormProps}.
 * @returns The form field.
 */
export function SignatureForm({
  form,
  fieldName,
  currentSignature,
  label,
  mode = 'own',
  onDeleteSignature,
}: SignatureFormProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | undefined>();
  const [isPadOpen, setIsPadOpen] = useState(false);
  const isAdmin = mode === 'admin';

  useEffect(() => {
    setPreview(currentSignature ?? undefined);
  }, [currentSignature]);

  // Manually trigger refine verification only when signature-related fields change
  // Using specific field names prevents race conditions during form initialization
  // where validation could fire before signatureUrl is properly set
  const { trigger, watch } = form;
  const urlFieldName = fieldName.replace('File', 'Url');
  useEffect(() => {
    const subscription = watch((_: unknown, { name }: { name?: string }) => {
      // Only trigger validation when signature fields change
      if (name === fieldName || name === urlFieldName) {
        trigger([fieldName]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger, fieldName, urlFieldName]);

  const updatePreview = (nextValue?: string) => {
    clearObjectUrl(preview);
    setPreview(nextValue);
  };

  const downloadSignature = () => {
    if (!currentSignature) {
      return;
    }

    downloadByUrl({
      url: currentSignature,
      filename: 'signature',
    });
  };

  const deleteSignature = () => {
    form.setValue(fieldName, null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    updatePreview();
    onDeleteSignature?.();
  };

  const drawLabel = isAdmin
    ? t('signature-form.btn.draw')
    : t('signature-form.btn.draw-own');

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem className="mb-4">
          <span className="font-medium text-sm leading-none">{label}</span>

          <div className="flex flex-col gap-3 rounded-md border bg-card p-3 sm:flex-row sm:items-center">
            <div className="flex h-20 w-full shrink-0 items-center justify-center overflow-hidden rounded border bg-white sm:w-48">
              {preview ? (
                <img
                  src={preview}
                  alt={t('signature-form.preview-alt')}
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-muted-foreground text-sm">
                  {t('signature-form.empty')}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPadOpen(true)}
              >
                <PenLineIcon />
                {preview ? t('signature-form.btn.edit') : drawLabel}
              </Button>

              {isAdmin && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => inputRef.current?.click()}
                >
                  <UploadIcon />
                  {t('signature-form.btn.import')}
                </Button>
              )}

              {currentSignature && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label={t('btn.download')}
                      onClick={downloadSignature}
                    >
                      <DownloadIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('btn.download')}</TooltipContent>
                </Tooltip>
              )}

              {isAdmin && preview && onDeleteSignature && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label={t('btn.delete')}
                      onClick={deleteSignature}
                    >
                      <TrashIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('btn.delete')}</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>

          {isAdmin && (
            <FormControl className="hidden">
              <Input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_SIGNATURE_FILE_INPUT}
                onChange={(event) => {
                  const { files, previewUrl } = getImageData(event);

                  updatePreview(previewUrl);
                  field.onChange(files[0]);
                  event.target.value = '';
                }}
                onBlur={field.onBlur}
              />
            </FormControl>
          )}

          <FormMessage />

          <SignaturePadDialog
            open={isPadOpen}
            onOpenChange={setIsPadOpen}
            hasExistingSignature={Boolean(preview)}
            onSave={(file) => {
              updatePreview(URL.createObjectURL(file));
              field.onChange(file);
            }}
          />
        </FormItem>
      )}
    />
  );
}
